import * as Mui from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { FieldArray, Form, Formik } from 'formik';
import { useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InputField } from '../../../../../components/form';
import { CloseDrawer } from '../../../../../components/form/close-drawer';
import { RuleTitle } from '../../../canteen-management/canteen/view-components/price-rule/components/rule-title';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

const paperProps = {
    style: {
        width: '550px',
        height: '100%',
        overflow: 'hidden'
    }
}

export const SubcategoriesDrawer = ({ onClose, formikProps, selectedSubCategoryId, ...props }) => {

    const formikRef = useRef(null)

    const { t } = useTranslation()

    const { categories } = formikProps.values;

    const selectedCategory = categories?.find(cat => cat.parentCategory.id === selectedSubCategoryId)


    const { existingParentNames, existingSubNames } = useMemo(() => {
        const categories = formikProps?.values?.categories?.filter(cat => cat.parentCategory.id !== selectedSubCategoryId) || [];

        return {
            existingParentNames: categories.map(c =>
                c.parentCategory?.name?.trim().toLowerCase()
            ).filter(Boolean),

            existingSubNames: categories.flatMap(c =>
                c.subCategories?.map(s => s.name?.trim().toLowerCase()) || []
            )
        };
    }, [formikProps, selectedSubCategoryId]);


    const validationSchema = Yup.object().shape({
        parentCategory: Yup.object().shape({
            name: Yup.string()
                .required(t("AUTH.VALIDATION.REQUIRED_FIELD"))
                .test('unique-parent-global', t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS"), function (value) {
                    if (!value) return true;
                    const inputVal = value.trim().toLowerCase();

                    // Check 1: Is it in the PREVIOUSLY ADDED list?
                    if (existingParentNames.includes(inputVal)) {
                        return this.createError({ message: t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS_IN_LIST", { name: value }) });
                    }

                    return true;
                }),
        }),
        subCategories: Yup.array().of(Yup.object().shape({
            name: Yup.string()
                .required(t("AUTH.VALIDATION.REQUIRED_FIELD"))
                .test('unique-sub-global', t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.SUB_CATEGORIES_MUST_BE_UNIQUE"), function (value) {
                    if (!value) return true;
                    const inputVal = value.trim().toLowerCase();

                    // Check 1: Is it in the PREVIOUSLY ADDED list?
                    if (existingSubNames.includes(inputVal)) {
                        return this.createError({ message: t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS_IN_LIST", { name: value }) });
                    }

                    // Check 2: Is it duplicated in the CURRENT form?
                    const { subCategories } = this.from[1].value;
                    const duplicates = subCategories.filter(c =>
                        c.name &&
                        c.name.trim().toLowerCase() === inputVal
                    );

                    return duplicates.length <= 1;
                })
        }))

    })


    const onSubmit = (values) => {


        const updatedCategories = categories.map((category) => {

            if (category.parentCategory.id === selectedCategory.parentCategory.id) {

                const updatedSubCategories = values.subCategories.map((subCat) => {
                    if (category?.subCategories?.find((subCategory) => subCategory.id === subCat.id)) {
                        return {
                            ...subCat,
                            name: subCat.name
                        }
                    }
                    return subCat;
                }).filter((subCat) => subCat !== null)

                return {
                    ...category,
                    parentCategory: {
                        ...category.parentCategory,
                        name: values.parentCategory.name
                    },
                    subCategories: updatedSubCategories
                }
            }
            return category;
        })

        formikProps.setFieldValue('categories', [...updatedCategories])
        onClose()

    }


    return <Mui.Drawer
        {...props}
        onClose={() => { }}
        anchor="right"
        PaperProps={paperProps}

    >
        <Mui.Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Formik
                innerRef={formikRef}
                initialValues={selectedCategory}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {(props) => (
                    <>
                        <Mui.Box className="pl-2 pr-2 pt-2 pb-1 d-flex justify-content-between align-items-center">
                            <RuleTitle title={t("ORGANIZATION.ONBOARD.SUBCATEGORY_DRAWER.TITLE", { name: props?.values?.parentCategory?.name })} subtitle={t("ORGANIZATION.ONBOARD.SUBCATEGORY_DRAWER.SUBTITLE")} />
                            <CloseDrawer onClose={() => { onClose() }} formikRef={formikRef} />
                        </Mui.Box>

                        <Mui.Typography className='pl-2 pr-2 pb-2 text-color-grey font-weight-normal' component={'span'} variant='body1'>
                            {t("ORGANIZATION.ONBOARD.SUBCATEGORY_DRAWER.TOTAL_SELECTED", { count: props?.values?.subCategories?.length })}
                        </Mui.Typography>
                        <Mui.Divider className="mb-2" />

                        <Mui.Box style={{ flex: 1, overflowY: 'auto', scrollbarGutter: 'stable' }}>

                            <Form className="default-form p-2">
                                <FieldArray
                                    name="subCategories"
                                    render={(arrayHelpers) => (
                                        <>
                                            <Mui.Grid container spacing={1}>
                                                <Mui.Grid item xs={12} className="form-group" style={{ padding: '0rem', margin: '0rem 0rem 3rem 0rem' }}>
                                                    <FieldLayout
                                                        inputComp={<InputField
                                                            name="parentCategory.name"
                                                            // label="Category Name"
                                                            type="text"
                                                            style={{ flex: 1 }}
                                                            inputProps={{ style: { fontSize: '2rem' } }}
                                                        />}
                                                        actionComp={<Add />}
                                                        onActionClick={() => {
                                                            arrayHelpers.push({ id: uuidv4(), name: '', products: [] })
                                                        }}
                                                    />
                                                </Mui.Grid>
                                            </Mui.Grid>
                                            <Mui.Box>
                                                <Mui.Grid container spacing={1}>
                                                    {props.values.subCategories.map((subCategory, index) => (
                                                        <Mui.Grid item xs={12} className="form-group" key={index} style={{ padding: '0rem', margin: '0rem 0rem 1rem 0rem' }}>
                                                            <FieldLayout
                                                                leadingComp={<SubdirectoryArrowRightIcon />}
                                                                inputComp={<InputField
                                                                    name={`subCategories.${index}.name`}
                                                                    type="text"
                                                                    style={{ flex: 1 }}
                                                                    inputProps={{ style: { fontSize: '2rem' } }}
                                                                />}
                                                                actionComp={<Remove />}
                                                                onActionClick={() => {
                                                                    arrayHelpers.remove(index)
                                                                }}
                                                            />
                                                        </Mui.Grid>
                                                    ))}
                                                </Mui.Grid>
                                            </Mui.Box>
                                        </>
                                    )}
                                />
                            </Form>

                        </Mui.Box>
                        <Mui.Divider />
                        <Mui.Box className='d-flex justify-content-end p-2 align-items-center'>
                            <Mui.Box className='d-flex justify-content-end' gridGap={3}>

                                <Mui.Button
                                    className="font-weight-normal no-border-btn text-transform-capitalize"
                                    type="button"
                                    variant="text"
                                    disableElevation
                                    onClick={() => { props.resetForm() }}
                                >
                                    {t("COMMON.BUTTON.RESET")}
                                </Mui.Button>
                                <CloseDrawer
                                    onClose={() => { onClose() }}
                                    formikRef={formikRef}
                                    customComponent={(handleClose) => (
                                        <Mui.Button
                                            className='btn-default mr-2'
                                            variant='outlined'
                                            color='primary'
                                            onClick={handleClose}
                                        >
                                            {t("ORGANIZATION.ONBOARD.SUBCATEGORY_DRAWER.CANCEL")}
                                        </Mui.Button>
                                    )}
                                />
                                {props?.dirty && <Mui.Button
                                    className='btn-default'
                                    variant='contained'
                                    color='primary'
                                    onClick={() => formikRef.current.handleSubmit()}
                                >
                                    {t("ORGANIZATION.ONBOARD.SUBCATEGORY_DRAWER.UPDATE")}
                                </Mui.Button>}
                            </Mui.Box>
                        </Mui.Box>
                    </>
                )}
            </Formik>
        </Mui.Box>
    </Mui.Drawer>
}


const FieldLayout = ({ leadingComp = null, inputComp, actionComp, onActionClick }) => {

    return <Mui.Box className='d-flex align-items-center'>
        {leadingComp && <Mui.Box className='mr-1'>
            {leadingComp}
        </Mui.Box>}
        {inputComp}
        <Mui.Box onClick={onActionClick} className='ml-1 pointer d-flex justify-content-center align-items-center' style={{ border: '1px solid #9aa1a780', borderRadius: 8, padding: '1rem' }}>
            {actionComp}
        </Mui.Box>
    </Mui.Box>
}